import { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Building2, RefreshCw, CheckCircle2 } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { toast } from 'sonner';

interface CompanyDataFixProps {
  accessToken: string;
}

export function CompanyDataFix({ accessToken }: CompanyDataFixProps) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleFix = async () => {
    setLoading(true);
    setResult(null);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/fix-company-data`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fix company data');
      }

      const data = await response.json();
      setResult(data);
      
      if (data.success) {
        toast.success('Company data fixed successfully');
        // Dispatch event to refresh user list
        window.dispatchEvent(new CustomEvent('pathkit-demo-users-created'));
      }
    } catch (error) {
      console.error('Error fixing company data:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to fix company data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Fix Company Data
            </CardTitle>
            <CardDescription>
              Ensure all System Admins are assigned to Pathkit and all users have company names populated
            </CardDescription>
          </div>
          <Button onClick={handleFix} disabled={loading}>
            {loading ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Fixing...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Fix Now
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground space-y-2">
            <p>This utility will:</p>
            <ul className="list-disc list-inside ml-2 space-y-1">
              <li>Create a "Pathkit" company if it doesn't exist</li>
              <li>Assign all System Admins to the Pathkit company</li>
              <li>Populate missing company names for all users</li>
            </ul>
          </div>

          {result && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-1">
                  <p className="font-medium">Fix completed.</p>
                  <ul className="text-sm space-y-1 mt-2">
                    {result.scopifyCompanyCreated && (
                      <li>✓ Created Pathkit company</li>
                    )}
                    {!result.scopifyCompanyCreated && (
                      <li>✓ Pathkit company already exists</li>
                    )}
                    <li>✓ Fixed {result.systemAdminsFixed} system admin assignments</li>
                    <li>✓ Fixed {result.companyNamesFixed} missing company names</li>
                  </ul>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
